import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import cn from 'classnames';

import { CAMPAIGNS_STATUSES, CAMPAIGNS_TYPES } from 'const';
import { ICampaign } from 'interfaces/campaigns';
import {
	getAdvertisingCampaignById,
	startAdvertisingCampaign,
} from 'services/advertisingCampaigns';
import { useSelector } from 'store';
import {
	selectCompanyProduct,
	selectHasCompanyRights,
} from 'store/companiesReducer';
import { selectActiveProductSlug } from 'store/productsReducer';
import { getExpireDate, getStartDate } from 'utils/date';
import Button from 'components/Button';
import Card from 'components/Card';
import StatusLabel from 'components/StatusLabel';
import Tooltip from 'components/Tooltip';
import { ReactComponent as MenuIcon } from 'assets/imgs/menu-dots.svg';
import classes from './advertisingCampaign.module.scss';

const ADVERTISING_TYPE_LABEL = {
	[CAMPAIGNS_TYPES.SUBSCRIPTION]: 'Рекламная подписка',
	[CAMPAIGNS_TYPES.GEO]: 'Приоритетное размещение в Картах и Навигаторе',
	[CAMPAIGNS_TYPES.WEB]: 'Реклама сайта или социальной сети',
};

const ADVERTISING_TYPE_DESCRIPTION = {
	[CAMPAIGNS_TYPES.SUBSCRIPTION]:
		'Есть все что в "Приоритетном размещении". Кроме этого сервис сам создаст рекламные объявления на основе вашей информации об организации и запустит их в интернете. Объявления появятся сразу на нескольких площадках Яндекса: Поиске, Картах, Услугах, Дзене, Рекламной сети. А также в Google и их Рекламной сети. Реклама будет вести клиентов на карточку вашей организации в Яндексе',
	[CAMPAIGNS_TYPES.GEO]:
		'Организация получит зеленую метку на картах Навигатора и Яндекс.Карт. Поднимется в поиске мест и адресов в обоих приложениях. Будет показываться в рекомендациях тем, кто ищет похожие организации, товары или услуги. Вы сможете следить за статистикой просмотров карточки и действий в ней',
	[CAMPAIGNS_TYPES.WEB]:
		'Рекламные объявления приведут пользователей к вам на сайт, в профиль или на пост в соцсети. Подходит, если продаёте услуги и товары онлайн, хотите увеличить аудиторию в соцсети или охват поста',
};

const AdvertisingCampaign: React.FC = () => {
	const { campaignId = '' } = useParams();
	const activeProductSlug = useSelector(selectActiveProductSlug);
	const hasCompanyRights = useSelector(selectHasCompanyRights);
	const companyPartner = useSelector(selectCompanyProduct);
	const [advertisingCampaign, setAdvertisingCampaign] =
		useState<ICampaign | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isStartingCampaign, setIsStartingCampaign] = useState(false);
	const [isOpenLinks, setIsOpenLinks] = useState(false);

	const campaignStatus =
		advertisingCampaign?.campaign_partner_info?.status || '';
	const isNotStarted = campaignStatus === CAMPAIGNS_STATUSES.WAITING;
	const isArchive =
		campaignStatus === CAMPAIGNS_STATUSES.FINISHED ||
		campaignStatus === CAMPAIGNS_STATUSES.STOPPED;
	const isActive = !isNotStarted && !isArchive;
	const isStartButtonVisible = isNotStarted && hasCompanyRights;
	const remainingDays =
		advertisingCampaign?.campaign_partner_info?.remainingDays || 0;
	const productCampaignId = advertisingCampaign?.meta?.id || '';

	const getAdvertisingCampaignData = async (): Promise<void> => {
		setIsLoading(true);
		try {
			const newAdvertisingCampaigns = await getAdvertisingCampaignById(
				activeProductSlug,
				campaignId
			);
			setAdvertisingCampaign(newAdvertisingCampaigns.data);
		} catch (err) {
			console.log(err);
		}
		setIsLoading(false);
	};

	const handleToggleLinks = (e: React.MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
		setIsOpenLinks(!isOpenLinks);
	};

	const handleOutsideClick = () => {
		setIsOpenLinks(false);
	};

	const handleStartCampaign = async () => {
		if (isStartingCampaign) return;

		setIsStartingCampaign(true);
		try {
			await startAdvertisingCampaign({
				slug: activeProductSlug,
				id: advertisingCampaign?.id || '',
				payload: {
					partner_type: 'yandex',
					budgetType:
						advertisingCampaign?.meta?.campaign_subtype || '',
					duration: advertisingCampaign?.meta?.duration || '',
				},
			});
			toast.success('Рекламная кампания успешно запущенна');
			getAdvertisingCampaignData();
		} catch (e) {
			toast.error('Не удалось запустить рекламную кампанию');
		}
		setIsStartingCampaign(false);
	};

	useEffect(() => {
		if (activeProductSlug) {
			getAdvertisingCampaignData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeProductSlug]);

	useEffect(() => {
		document.addEventListener('click', handleOutsideClick);
		return () => {
			document.removeEventListener('click', handleOutsideClick);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getPeriod = () => {
		const duration = advertisingCampaign?.meta?.duration || '';
		const period =
			isNotStarted && duration
				? `${duration} дней`
				: isActive && duration && remainingDays
				? `${getStartDate(
						Number(duration),
						remainingDays
				  )}-${getExpireDate(remainingDays)}`
				: '';
		return period;
	};

	const header = (
		<div className={classes.header}>
			<div className={classes.title}>
				{/* TODO: add skeleton loader */}
				{advertisingCampaign?.name || ''}
				<StatusLabel
					className={classes.status}
					status={campaignStatus}
					isPaid={advertisingCampaign?.state === 'paid'}
				/>
			</div>
			{isStartButtonVisible && (
				<Button
					className={classes.startButton}
					text="Запустить"
					onClick={handleStartCampaign}
				/>
			)}
			<div
				className={cn(classes.menuIcon, {
					[classes.menuIcon__withStart]: isStartButtonVisible,
				})}
				onClick={handleToggleLinks}
			>
				<MenuIcon />
				{isOpenLinks && (
					<div className={classes.menu}>
						{!isArchive && (
							<a
								href={`https://yandex.ru/business/priority/campaign/${productCampaignId}/media`}
								target="_blank"
								rel="noreferrer"
								className={classes.menuItem}
							>
								Добавить рекламные материалы
							</a>
						)}
						{!isArchive && (
							<a
								href={`https://yandex.ru/business/subscription/campaign/${productCampaignId}/tuning/business-card`}
								target="_blank"
								rel="noreferrer"
								className={classes.menuItem}
							>
								Настроить параметры показа рекламы
							</a>
						)}
						{!isNotStarted && (
							<a
								href={`https://yandex.ru/business/priority/campaign/${productCampaignId}/statistic`}
								target="_blank"
								rel="noreferrer"
								className={classes.menuItem}
							>
								Статистика рекламной кампании
							</a>
						)}
						<a
							href={`https://yandex.ru/sprav/${
								companyPartner?.id || ''
							}`}
							target="_blank"
							rel="noreferrer"
							className={classes.menuItem}
						>
							Информация об организации
						</a>
					</div>
				)}
			</div>
		</div>
	);

	const advertisingCampaignInfo: { [key: string]: string | number } = {
		'Номер рекламной кампании': advertisingCampaign?.meta?.id || '--',
		'Тип рекламы':
			ADVERTISING_TYPE_LABEL[advertisingCampaign?.meta?.type || ''] ||
			'--',
		'Период размещения': getPeriod() || '--',
		Организация: companyPartner?.name || '--',
		Адрес: companyPartner?.address || '--',
	};
	const info = (
		<Card isLoading={isLoading}>
			{/* TODO: add skeleton loader */}
			{!isLoading && (
				<div className={classes.advertisingCampaignInfo}>
					{Object.keys(advertisingCampaignInfo).map((infoTitle) => (
						<div className={classes.info} key={infoTitle}>
							<div className={classes.infoTitle}>{infoTitle}</div>
							<div className={classes.infoText}>
								{advertisingCampaignInfo[infoTitle]}
							</div>
							{infoTitle === 'Тип рекламы' && (
								<Tooltip
									className={classes.infoIcon}
									text={
										ADVERTISING_TYPE_DESCRIPTION[
											advertisingCampaign?.meta?.type ||
												''
										]
									}
								/>
							)}
						</div>
					))}
				</div>
			)}
		</Card>
	);
	return (
		<div className={classes.advertisingCampaign}>
			{header}
			{info}
		</div>
	);
};

export default AdvertisingCampaign;
